import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    Edit2, Send, XCircle, Users, Award, Calendar,
    Building2, Clock, FileText, CheckCircle, BarChart2, Eye, Mail, Phone, MapPin
} from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardBody, Button, Badge, ConfirmDialog, toast, Tabs, Modal
} from '../../components/common';
import { rfqApi } from '../../services/api';
import VendorInviteModal from './VendorInviteModal';
import QuotationComparison from './QuotationComparison';
import './RFQDetail.css';

export default function RFQDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [actionType, setActionType] = useState('');
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [selectedVendor, setSelectedVendor] = useState(null);

    const { data: rfq, isLoading } = useQuery({
        queryKey: ['rfq', id],
        queryFn: () => rfqApi.get(id),
        select: (res) => res.data?.data
    });

    const actionMutation = useMutation({
        mutationFn: ({ action }) => {
            switch (action) {
                case 'publish': return rfqApi.publish(id);
                case 'close': return rfqApi.close(id);
                default: throw new Error('Unknown action');
            }
        },
        onSuccess: (_, { action }) => {
            toast.success(`RFQ ${action}ed successfully`);
            queryClient.invalidateQueries(['rfq', id]);
            setActionType('');
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Action failed');
        }
    });

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status) => {
        const config = {
            'draft': 'default',
            'published': 'info',
            'in_progress': 'warning',
            'closed': 'default',
            'awarded': 'success',
            'cancelled': 'danger'
        };
        return <Badge variant={config[status] || 'default'} size="lg">{status?.replace('_', ' ')}</Badge>;
    };

    if (isLoading) {
        return <div className="loading-state">Loading...</div>;
    }

    if (!rfq) {
        return <div className="error-state">RFQ not found</div>;
    }

    const canEdit = rfq.status === 'draft';
    const canPublish = rfq.status === 'draft' && rfq.invitedVendors?.length > 0;
    const canClose = rfq.status === 'published';
    const canAward = rfq.status === 'closed' && rfq.quotations?.length > 0;

    const lines = rfq.lines || rfq.items || [];

    const tabs = [
        {
            key: 'details',
            label: 'Details',
            icon: FileText,
            content: (
                <div className="rfq-details">
                    <div className="detail-section">
                        <h4>Description</h4>
                        <p>{rfq.description || 'No description provided'}</p>
                    </div>

                    <div className="detail-section">
                        <h4>Items</h4>
                        <table className="detail-table">
                            <thead>
                                <tr>
                                    <th>Item</th>
                                    <th>Description</th>
                                    <th>Qty</th>
                                    <th>UOM</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            <div className="item-info">
                                                <span className="item-code">{item.itemCode || item.catalogItem?.itemCode}</span>
                                                <span className="item-name">{item.itemName || item.catalogItem?.name}</span>
                                            </div>
                                        </td>
                                        <td>{item.specifications || item.description || '-'}</td>
                                        <td>{item.quantity}</td>
                                        <td>{item.uomCode || item.catalogItem?.unitOfMeasure?.code || 'PCS'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {rfq.evaluationCriteria && (
                        <div className="detail-section">
                            <h4>Evaluation Criteria</h4>
                            <p>{rfq.evaluationCriteria}</p>
                        </div>
                    )}

                    {rfq.termsAndConditions && (
                        <div className="detail-section">
                            <h4>Terms & Conditions</h4>
                            <p>{rfq.termsAndConditions}</p>
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'vendors',
            label: `Vendors (${rfq.invitedVendors?.length || 0})`,
            icon: Users,
            content: (
                <div className="invited-vendors">
                    {rfq.invitedVendors?.length === 0 ? (
                        <div className="empty-vendors">
                            <Users size={36} />
                            <p>No vendors invited yet</p>
                            {canEdit && (
                                <Button size="sm" onClick={() => setShowInviteModal(true)}>
                                    Invite Vendors
                                </Button>
                            )}
                        </div>
                    ) : (
                        <div className="vendors-grid">
                            {rfq.invitedVendors?.map((invitation) => (
                                <div key={invitation.id} className="vendor-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedVendor(invitation.vendor)}>
                                    <div className="vendor-icon">
                                        <Building2 size={20} />
                                    </div>
                                    <div className="vendor-info">
                                        <h5>{invitation.vendor?.companyName || 'Unknown Vendor'}</h5>
                                        <p>{invitation.vendor?.email || invitation.vendor?.vendorCode || '-'}</p>
                                    </div>
                                    <Badge variant={invitation.status === 'submitted' ? 'success' : 'warning'}>
                                        {invitation.status === 'submitted' ? 'Submitted' : 'Pending'}
                                    </Badge>
                                    <button className="vendor-view-btn" onClick={(e) => { e.stopPropagation(); setSelectedVendor(invitation.vendor); }} title="View Vendor Details">
                                        <Eye size={16} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'quotations',
            label: `Quotations (${rfq.quotations?.length || 0})`,
            icon: BarChart2,
            content: <QuotationComparison rfqId={id} quotations={rfq.quotations || []} items={lines} />
        }
    ];

    return (
        <div className="rfq-detail-page">
            <PageHeader
                title={rfq.rfqNumber}
                breadcrumbs={[
                    { label: 'RFQ', path: '/rfq' },
                    { label: rfq.rfqNumber }
                ]}
                actions={
                    <div className="rfq-actions">
                        {canEdit && (
                            <>
                                <Button variant="secondary" icon={Edit2} onClick={() => navigate(`/rfq/${id}/edit`)}>
                                    Edit
                                </Button>
                                <Button variant="secondary" icon={Users} onClick={() => setShowInviteModal(true)}>
                                    Invite Vendors
                                </Button>
                            </>
                        )}
                        {canPublish && (
                            <Button icon={Send} onClick={() => setActionType('publish')}>
                                Publish
                            </Button>
                        )}
                        {canClose && (
                            <Button variant="secondary" icon={XCircle} onClick={() => setActionType('close')}>
                                Close RFQ
                            </Button>
                        )}
                        {canAward && (
                            <Button icon={Award} onClick={() => navigate(`/rfq/${id}/award`)}>
                                Award Vendor
                            </Button>
                        )}
                    </div>
                }
            />

            <div className="rfq-detail-content">
                <div className="rfq-detail-main">
                    <Card>
                        <CardBody>
                            <div className="rfq-header">
                                <div>
                                    <h2>{rfq.title}</h2>
                                    <p className="rfq-number">{rfq.rfqNumber}</p>
                                </div>
                                {getStatusBadge(rfq.status)}
                            </div>

                            <div className="rfq-meta-grid">
                                <div className="meta-item">
                                    <Calendar size={16} />
                                    <span className="meta-label">Deadline</span>
                                    <span className="meta-value">{formatDate(rfq.submissionDeadline)}</span>
                                </div>
                                <div className="meta-item">
                                    <Clock size={16} />
                                    <span className="meta-label">Created</span>
                                    <span className="meta-value">{formatDate(rfq.createdAt)}</span>
                                </div>
                                <div className="meta-item">
                                    <FileText size={16} />
                                    <span className="meta-label">From PR</span>
                                    <span className="meta-value">{rfq.purchaseRequest?.prNumber || '-'}</span>
                                </div>
                                <div className="meta-item">
                                    <Users size={16} />
                                    <span className="meta-label">Vendors</span>
                                    <span className="meta-value">{rfq.invitedVendors?.length || 0} invited</span>
                                </div>
                            </div>
                        </CardBody>
                    </Card>

                    <Card>
                        <Tabs tabs={tabs} defaultTab="details" onChange={setActiveTab} />
                    </Card>
                </div>
            </div>

            <VendorInviteModal
                rfqId={id}
                isOpen={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                onSuccess={() => {
                    setShowInviteModal(false);
                    queryClient.invalidateQueries(['rfq', id]);
                }}
                excludeVendorIds={rfq.invitedVendors?.map(v => v.id) || []}
            />

            <ConfirmDialog
                isOpen={!!actionType}
                onClose={() => setActionType('')}
                onConfirm={() => actionMutation.mutate({ action: actionType })}
                title={`${actionType.charAt(0).toUpperCase() + actionType.slice(1)} RFQ`}
                message={`Are you sure you want to ${actionType} this RFQ?`}
                confirmText={actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                loading={actionMutation.isPending}
            />

            {/* Vendor Detail Modal */}
            <Modal
                isOpen={!!selectedVendor}
                onClose={() => setSelectedVendor(null)}
                title="Vendor Details"
                size="lg"
            >
                {selectedVendor && (
                    <div className="vendor-detail-modal">
                        <div className="vendor-header">
                            <div className="vendor-avatar">
                                <Building2 size={32} />
                            </div>
                            <div>
                                <h3>{selectedVendor.companyName}</h3>
                                <span className="vendor-code">{selectedVendor.vendorCode}</span>
                                {selectedVendor.businessType && (
                                    <Badge variant="info" style={{ marginLeft: '0.5rem' }}>
                                        {selectedVendor.businessType.replace('_', ' ')}
                                    </Badge>
                                )}
                            </div>
                            {selectedVendor.overallRating > 0 && (
                                <div className="vendor-rating">
                                    <span className="rating-value">★ {parseFloat(selectedVendor.overallRating).toFixed(1)}</span>
                                </div>
                            )}
                        </div>

                        <div className="vendor-detail-sections">
                            {/* Contact Information */}
                            <div className="detail-section">
                                <h4>Contact Information</h4>
                                <div className="vendor-details-grid">
                                    <div className="detail-row">
                                        <Mail size={16} />
                                        <span className="label">Email</span>
                                        <span className="value">{selectedVendor.email || '-'}</span>
                                    </div>
                                    <div className="detail-row">
                                        <Phone size={16} />
                                        <span className="label">Phone</span>
                                        <span className="value">{selectedVendor.phone || '-'}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Contact Person */}
                            {(selectedVendor.contactPerson || selectedVendor.contactPhone || selectedVendor.contactEmail) && (
                                <div className="detail-section">
                                    <h4>Contact Person</h4>
                                    <div className="vendor-details-grid">
                                        {selectedVendor.contactPerson && (
                                            <div className="detail-row">
                                                <Users size={16} />
                                                <span className="label">Name</span>
                                                <span className="value">{selectedVendor.contactPerson}</span>
                                            </div>
                                        )}
                                        {selectedVendor.contactPhone && (
                                            <div className="detail-row">
                                                <Phone size={16} />
                                                <span className="label">Phone</span>
                                                <span className="value">{selectedVendor.contactPhone}</span>
                                            </div>
                                        )}
                                        {selectedVendor.contactEmail && (
                                            <div className="detail-row">
                                                <Mail size={16} />
                                                <span className="label">Email</span>
                                                <span className="value">{selectedVendor.contactEmail}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Address */}
                            <div className="detail-section">
                                <h4>Address</h4>
                                <div className="vendor-details-grid">
                                    <div className="detail-row full-width">
                                        <MapPin size={16} />
                                        <span className="label">Address</span>
                                        <span className="value">
                                            {[selectedVendor.address, selectedVendor.city, selectedVendor.province].filter(Boolean).join(', ') || '-'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-actions">
                            <Button variant="secondary" onClick={() => setSelectedVendor(null)}>
                                Close
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
}
